import { Auth, API } from 'aws-amplify';

class ApiService {
  async makeRequest(path, method = 'GET', data = null) {
    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      if (data) {
        config.body = data;
      }

      console.log(`API Request: ${method} ${path}`);
      const methodName = method.toLowerCase() === 'delete' ? 'del' : method.toLowerCase();
      const result = await API[methodName]('TransportAPI', path, config);
      console.log(`API Response for ${path}:`, JSON.stringify(result, null, 2));
      return result;
    } catch (error) {
      console.error(`API Error for ${path}:`, error);
      // Extract error message from response
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  // Dashboard
  async getDashboardData() {
    return this.makeRequest('/admin');
  }

  // Reports
  async getGrossIncomeReport(driverId, startDate, endDate, truckId, auditAccess = false) {
    const params = new URLSearchParams({ report_type: 'gross_income' });
    if (driverId) params.append('driver_id', driverId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (truckId) params.append('truck', truckId);
    if (auditAccess) params.append('audit_access', 'true');
    return this.makeRequest(`/admin?${params}`);
  }

  async getExpensesReport(driverId, startDate, endDate, truckId, auditAccess = false) {
    const params = new URLSearchParams({ report_type: 'expenses' });
    if (driverId) params.append('driver_id', driverId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (truckId) params.append('truck', truckId);
    if (auditAccess) params.append('audit_access', 'true');
    return this.makeRequest(`/admin?${params}`);
  }

  async getFuelConsumptionReport(driverId, startDate, endDate, truckId, auditAccess = false) {
    const params = new URLSearchParams({ report_type: 'fuel_consumption' });
    if (driverId) params.append('driver_id', driverId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (truckId) params.append('truck', truckId);
    if (auditAccess) params.append('audit_access', 'true');
    return this.makeRequest(`/admin?${params}`);
  }

  async getTripsReport(driverId, date) {
    const params = new URLSearchParams({ report_type: 'trips' });
    if (driverId) params.append('driver_id', driverId);
    if (date) params.append('date', date);
    return this.makeRequest(`/admin?${params}`);
  }

  // Driver Management
  async addDriver(driverData) {
    return this.makeRequest('/admin', 'POST', { action: 'add_driver', data: driverData });
  }

  async updateDriver(driverId, driverData) {
    return this.makeRequest('/admin', 'POST', { action: 'update_driver', data: { driver_id: driverId, ...driverData } });
  }

  async generateDriverUploadUrl(driverId, fileName, fileType, documentType) {
    return this.makeRequest('/admin', 'POST', {
      action: 'generate_driver_upload_url',
      data: { driver_id: driverId, fileName, fileType, documentType }
    });
  }

  async updateDriverDocuments(driverId, documents) {
    return this.makeRequest('/admin', 'POST', {
      action: 'update_driver_documents',
      data: { driver_id: driverId, documents }
    });
  }

  // Truck Management
  async addTruck(truckData) {
    return this.makeRequest('/admin', 'POST', { action: 'add_truck', data: truckData });
  }

  async updateTruck(truckId, truckData) {
    return this.makeRequest('/admin', 'POST', { action: 'update_truck', data: { truck_id: truckId, ...truckData } });
  }

  async generateTruckUploadUrl(truckId, fileName, fileType, documentType) {
    return this.makeRequest('/admin', 'POST', {
      action: 'generate_truck_upload_url',
      truck_id: truckId,
      fileName,
      fileType,
      documentType
    });
  }

  async updateTruckDocuments(truckId, documents) {
    return this.makeRequest('/admin', 'POST', {
      action: 'update_truck_documents',
      truck_id: truckId,
      documents
    });
  }

  async associateDriverTruck(driverId, truckId) {
    return this.makeRequest('/admin', 'POST', { 
      action: 'associate_driver_truck', 
      data: { driver_id: driverId, truck_id: truckId } 
    });
  }



  // Admin Actions - Create on behalf of drivers
  async createTrip(tripData) {
    return this.makeRequest('/trips', 'POST', tripData);
  }

  async createFuelRequest(fuelData) {
    return this.makeRequest('/fuel', 'POST', fuelData);
  }

  async createExpense(expenseData) {
    return this.makeRequest('/expenses', 'POST', expenseData);
  }

  async deleteExpense(driverId, expenseId) {
    return this.makeRequest(`/expenses/${expenseId}?driver_id=${driverId}`, 'DELETE');
  }

  async deleteFuel(driverId, fuelId) {
    return this.makeRequest(`/fuel/${fuelId}?driver_id=${driverId}`, 'DELETE');
  }

  async getDrivers() {
    const data = await this.makeRequest('/admin');
    return data.drivers || [];
  }

  async getTrucks() {
    const data = await this.makeRequest('/admin');
    return { trucks: data.trucks || [] };
  }

  async getLocations() {
    // Updated to use admin endpoint for locations
    return this.makeRequest('/admin?report_type=locations');
  }

  async addLocation(locationName, locationType = 'general') {
    return this.makeRequest('/admin', 'POST', {
      action: 'add_location',
      data: {
        location_name: locationName,
        location_type: locationType
      }
    });
  }

  // Get data for loading existing entries
  async getTrips(driverId = null, date = null) {
    const params = new URLSearchParams();
    if (driverId) params.append('driver_id', driverId);
    if (date) params.append('date', date);
    const url = `/trips${params.toString() ? '?' + params : ''}`;
    console.log('API: getTrips URL:', url);
    return this.makeRequest(url);
  }

  async getFuelRequests(driverId = null, date = null) {
    const params = new URLSearchParams();
    if (driverId) params.append('driver_id', driverId);
    if (date) params.append('date', date);
    const url = `/fuel${params.toString() ? '?' + params : ''}`;
    console.log('API: getFuelRequests URL:', url);
    return this.makeRequest(url);
  }

  async getExpenses(driverId = null, date = null) {
    const params = new URLSearchParams();
    if (driverId) params.append('driver_id', driverId);
    if (date) params.append('date', date);
    const url = `/expenses${params.toString() ? '?' + params : ''}`;
    console.log('API: getExpenses URL:', url);
    return this.makeRequest(url);
  }

  // Fuel approval methods
  async getPendingFuelRequests() {
    return this.makeRequest('/admin?report_type=pending_fuel');
  }

  async approveFuelRequest(driverId, fuelId, status, adminNotes = '') {
    return this.makeRequest('/admin', 'POST', {
      action: 'approve_fuel',
      data: {
        driver_id: driverId,
        fuel_id: fuelId,
        status: status,
        admin_notes: adminNotes
      }
    });
  }

  // User Management APIs
  async getUsers() {
    return this.makeRequest('/users');
  }

  async getUser(userId) {
    return this.makeRequest(`/users/${userId}`);
  }

  async createUser(userData) {
    return this.makeRequest('/users', 'POST', userData);
  }

  async updateUser(userId, userData) {
    return this.makeRequest(`/users/${userId}`, 'PUT', userData);
  }

  async deleteUser(userId) {
    return this.makeRequest(`/users/${userId}`, 'DELETE');
  }

  async resetPassword(userId) {
    return this.makeRequest('/users', 'POST', { action: 'reset_password', userId });
  }

  // Assignment Management
  async setAssignment(assignmentData) {
    return this.makeRequest('/admin', 'POST', {
      action: 'set_assignment',
      data: assignmentData
    });
  }

  async getAssignment(driverId, date) {
    return this.makeRequest('/admin', 'POST', {
      action: 'get_assignments',
      data: { driver_id: driverId, date: date }
    });
  }

  async deleteAssignment(driverId, date) {
    return this.makeRequest('/admin', 'POST', {
      action: 'delete_assignment',
      data: { driver_id: driverId, date: date }
    });
  }

  // AI Analysis
  async getAIAnalysis(startDate, endDate, truckId = null, customPrompt = null) {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (truckId) params.append('truck_id', truckId);
    if (customPrompt) params.append('custom_prompt', encodeURIComponent(customPrompt));
    return this.makeRequest(`/analysis?${params}`);
  }

  // Pricing Management
  async getFuelPrices() {
    return this.makeRequest('/admin?report_type=fuel_prices');
  }

  async addFuelPrice(data) {
    return this.makeRequest('/admin', 'POST', { action: 'add_fuel_price', data });
  }

  async getPriceCards() {
    return this.makeRequest('/admin?report_type=price_cards');
  }

  async addPriceCard(data) {
    return this.makeRequest('/admin', 'POST', { action: 'add_price_card', data });
  }

  async updatePriceCard(priceCardId, data) {
    return this.makeRequest('/admin', 'POST', { action: 'update_price_card', data: { price_card_id: priceCardId, ...data } });
  }

  async calculateTripPrice(data) {
    return this.makeRequest('/admin', 'POST', { action: 'calculate_trip_price', data });
  }

  async generatePriceCard(data) {
    return this.makeRequest('/admin', 'POST', { action: 'generate_price_card', data });
  }

  async saveGeneratedPriceCard(cardData) {
    return this.makeRequest('/admin', 'POST', { action: 'save_generated_price_card', data: cardData });
  }
}

export default new ApiService();